import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Store, PartyPopper, Wine, CalendarDays, Radar, ArrowRight, Flame, Search, Navigation, Sparkles, Trophy } from "lucide-react";
import LandingHero from "@/components/app/LandingHero";

const PINS = {
  curtir: [
    { x: 22, y: 30, kind: "adega", label: "Adega aberta" },
    { x: 62, y: 22, kind: "role", label: "Resenha rolando" },
    { x: 44, y: 55, kind: "promo", label: "Promo do dia" },
    { x: 74, y: 64, kind: "game", label: "Society · faltam 2" },
    { x: 30, y: 74, kind: "adega", label: "Gelada aqui" },
  ],
  adega: [
    { x: 48, y: 46, kind: "mine", label: "Sua adega" },
    { x: 24, y: 26, kind: "people", label: "+1 cliente" },
    { x: 72, y: 30, kind: "people", label: "Viu o catálogo" },
    { x: 66, y: 72, kind: "people", label: "Chamou no Zap" },
    { x: 28, y: 70, kind: "people", label: "Como chegar" },
  ],
};

const PIN_STYLE = {
  adega: "bg-[#FFC700] text-black", role: "bg-[#ff7bbe] text-black", promo: "bg-emerald-400 text-black", game: "bg-emerald-500 text-black",
  mine: "bg-[#FFC700] text-black ring-4 ring-[#FFC700]/40 scale-125", people: "bg-white/90 text-black",
};
const PIN_ICON = { adega: Wine, role: PartyPopper, promo: Flame, mine: Store, people: Sparkles, game: Trophy };

function Pin({ p, i }) {
  const Icon = PIN_ICON[p.kind];
  return (
    <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0, opacity: 0 }}
      transition={{ delay: i * 0.12, type: "spring", stiffness: 260, damping: 18 }}
      className="absolute -translate-x-1/2 -translate-y-1/2 group" style={{ left: `${p.x}%`, top: `${p.y}%` }} data-testid={`sim-pin-${i}`}>
      <span className="absolute inset-0 rounded-full bg-[#FFC700]/40 animate-ping" />
      <div className={`relative h-9 w-9 rounded-full grid place-items-center shadow-[0_0_18px_rgba(255,199,0,0.55)] cursor-pointer transition-transform group-hover:scale-125 ${PIN_STYLE[p.kind]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="absolute left-1/2 -translate-x-1/2 top-11 whitespace-nowrap text-[10px] font-semibold px-2 py-0.5 rounded-full bg-black/80 border border-white/10 opacity-90 group-hover:opacity-100">{p.label}</div>
    </motion.div>
  );
}

function Simulator() {
  const [mode, setMode] = useState("curtir");
  return (
    <div className="grid lg:grid-cols-2 gap-10 items-center">
      <div className="space-y-6">
        <div className="inline-flex p-1 rounded-full bg-white/5 border border-white/10" data-testid="sim-mode-toggle">
          {[["curtir", "Vou Curtir", PartyPopper], ["adega", "Sou Adega/Comércio", Store]].map(([k, l, Icon]) => (
            <button key={k} onClick={() => setMode(k)} data-testid={`sim-mode-${k}`}
              className={`flex items-center gap-2 h-11 px-5 rounded-full text-sm font-semibold transition-colors ${mode === k ? "bg-[#FFC700] text-black shadow-[0_0_24px_rgba(255,199,0,0.5)]" : "text-white/70 hover:text-white"}`}>
              <Icon className="w-4 h-4" /> {l}
            </button>
          ))}
        </div>
        <ol className="space-y-3">
          {(mode === "curtir"
            ? [
              ["Encontre", "Rolês, jogos e adegas abertas acendem no mapa de Marília."],
              ["Organize seu rolê", "Toque no + e escolha \"Criar rolê\": nome, tipo, endereço, data e horário. Publicou, já aparece no mapa."],
              ["Feche o time", "No +, escolha \"Fechar time\": esporte, local, horário e quantas vagas faltam. A galera entra no jogo e o contador atualiza."],
              ["Confirme e curta", "Toque em \"Eu vou\", veja quem vai, combine o que levar e poste os momentos do rolê."],
            ]
            : [["Cadastre", "Sua adega vira um pin fixo com sua foto."], ["Apareça", "Quem está nos rolês perto te encontra."], ["Venda", "Clientes chamam direto no seu WhatsApp."]]
          ).map(([t, d], i) => (
            <motion.li key={mode + t} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}
              className="flex gap-4 items-start rounded-2xl p-4 bg-white/[0.03] border border-white/10 hover:border-[#FFC700]/50 transition-colors">
              <span className="h-9 w-9 shrink-0 rounded-full bg-[#FFC700] text-black font-extrabold grid place-items-center">{i + 1}</span>
              <div><div className="font-bold heading">{t}</div><div className="text-sm text-white/60">{d}</div></div>
            </motion.li>
          ))}
        </ol>
      </div>
      <div className="mx-auto w-[290px] h-[560px] rounded-[44px] border-[10px] border-[#1c1c1c] bg-[#0d0d0d] shadow-[0_0_60px_rgba(255,199,0,0.18)] relative overflow-hidden" data-testid="sim-phone">
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <svg className="absolute inset-0 w-full h-full opacity-50" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d="M0 40 Q 40 30 100 55" stroke="#FFC700" strokeOpacity="0.35" strokeWidth="1.2" fill="none" />
          <path d="M30 0 Q 45 50 35 100" stroke="#ffffff" strokeOpacity="0.15" strokeWidth="1" fill="none" />
        </svg>
        <div className="absolute top-4 inset-x-4 rounded-2xl bg-black/60 backdrop-blur-xl border border-white/10 px-3 py-2 flex items-center gap-2 text-xs text-white/70 z-10">
          <Search className="w-3.5 h-3.5" /> {mode === "curtir" ? "Rolês perto de você" : "Sua loja no mapa"}
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={mode} className="absolute inset-0" exit={{ opacity: 0 }}>
            {PINS[mode].map((p, i) => <Pin key={i} p={p} i={i} />)}
          </motion.div>
        </AnimatePresence>
        <motion.div key={`card-${mode}`} initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.6 }}
          className="absolute bottom-4 inset-x-4 rounded-2xl bg-black/70 backdrop-blur-xl border border-[#FFC700]/40 p-3 z-10">
          <div className="text-[10px] text-[#FFC700] font-bold uppercase tracking-wider">{mode === "curtir" ? "Aberta agora" : "Ao vivo"}</div>
          <div className="text-sm font-bold">{mode === "curtir" ? "Adega do Bairro · 450 m" : "12 pessoas viram sua adega hoje"}</div>
          <div className="mt-2 flex gap-2 text-[11px]">
            <span className="px-2 py-1 rounded-full bg-[#FFC700] text-black font-semibold">{mode === "curtir" ? "Ver catálogo" : "Ver números"}</span>
            <span className="px-2 py-1 rounded-full bg-white/10 flex items-center gap-1"><Navigation className="w-3 h-3" /> {mode === "curtir" ? "Como chegar" : "WhatsApp"}</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

const reveal = { initial: { opacity: 0, y: 40 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, amount: 0.2 }, transition: { duration: 0.6 } };

const FEATURES = [
  [Radar, "Mapa em Tempo Real", "Encontre o que está aberto perto de você."],
  [Wine, "Adegas & Drinks", "Descubra onde encostar para pegar a sua bebida."],
  [CalendarDays, "Eventos & Festas", "Veja a programação da cidade atualizada."],
  [Store, "Visibilidade Local", "Para comerciantes colocarem sua marca no radar de Marília."],
];

const go = (path) => () => { window.location.href = path; };
const Logo = () => <span className="heading text-2xl font-extrabold">Enkosta<span className="text-[#FFC700]">.</span></span>;

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white overflow-x-hidden" data-testid="landing-page">
      <header className="fixed top-0 inset-x-0 z-50 bg-black/30 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between gap-4">
          <a href="#topo" data-testid="landing-logo"><Logo /></a>
          <nav className="hidden md:flex gap-8 text-sm text-white/70">
            <a href="#como-funciona" className="hover:text-[#FFC700] transition-colors" data-testid="nav-como-funciona">Como Funciona</a>
            <a href="#explorar" className="hover:text-[#FFC700] transition-colors" data-testid="nav-explorar">Explorar</a>
            <a href="#para-adegas" className="hover:text-[#FFC700] transition-colors" data-testid="nav-para-adegas">Para Adegas</a>
          </nav>
          <button onClick={go("/login")} className="h-10 px-4 rounded-full bg-[#FFC700] text-black text-sm font-bold hover:shadow-[0_0_24px_rgba(255,199,0,0.6)] transition-shadow" data-testid="landing-login-button">Entrar / Criar Conta</button>
        </div>
      </header>

      <LandingHero go={go} />

      <section id="como-funciona" className="py-24 px-5 border-t border-white/5">
        <motion.div {...reveal} className="max-w-6xl mx-auto">
          <p className="text-[#FFC700] text-sm font-semibold mb-2">Como Enkostar</p>
          <h2 className="heading text-3xl md:text-4xl font-extrabold mb-12">Teste agora, sem baixar nada.</h2>
          <Simulator />
        </motion.div>
      </section>

      <section id="explorar" className="py-24 px-5">
        <div className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map(([Icon, t, d], i) => (
            <motion.div key={t} {...reveal} transition={{ duration: 0.5, delay: i * 0.08 }} id={i === 3 ? "para-adegas" : undefined}
              className="group rounded-3xl p-6 bg-white/[0.03] border border-white/10 backdrop-blur-xl hover:-translate-y-1 hover:border-[#FFC700]/60 hover:shadow-[0_0_40px_rgba(255,199,0,0.15)] transition-all" data-testid={`feature-card-${i}`}>
              <div className="h-12 w-12 rounded-2xl bg-[#FFC700]/15 text-[#FFC700] grid place-items-center mb-5 group-hover:bg-[#FFC700] group-hover:text-black transition-colors"><Icon className="w-6 h-6" /></div>
              <div className="heading font-bold text-lg">{t}</div>
              <p className="text-sm text-white/60 mt-1">{d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-24 px-5">
        <motion.div {...reveal} className="max-w-5xl mx-auto rounded-[36px] p-10 md:p-16 bg-[#121212] border border-[#FFC700]/30 relative overflow-hidden text-center">
          <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-[#FFC700]/15 blur-3xl" />
          <h2 className="heading text-3xl md:text-5xl font-extrabold relative">Pronto para <span className="text-[#FFC700]">encostar</span> no melhor rolê?</h2>
          <button onClick={go("/login")} className="relative mt-8 h-16 px-10 rounded-full bg-[#FFC700] text-black font-extrabold text-lg shadow-[0_0_40px_rgba(255,199,0,0.5)] hover:scale-[1.04] transition-transform" data-testid="bottom-cta">Acessar a Plataforma Agora</button>
        </motion.div>
      </section>

      <footer className="border-t border-white/5 py-10 px-5">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-4 items-center justify-between text-sm text-white/50">
          <div className="flex items-center gap-3"><Logo /><span>© {new Date().getFullYear()} Enkosta - Marília/SP</span></div>
          <div className="flex gap-5">
            <a href="/termos" className="hover:text-[#FFC700]" data-testid="footer-terms">Termos de Uso</a>
            <a href="/privacidade" className="hover:text-[#FFC700]" data-testid="footer-privacy">Política de Privacidade</a>
            <a href="mailto:Pedrosixx16@gmail.com" className="hover:text-[#FFC700]" data-testid="footer-contact">Contato</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
